import * as DialogPrimitive from "@radix-ui/react-dialog";
import { X } from "lucide-react";
import {
  Children,
  forwardRef,
  isValidElement,
  useEffect,
  useState,
  type HTMLAttributes,
  type ReactNode,
} from "react";
import { IconButton } from "@/components/ui/icon-button";
import { cn } from "@/lib/utils";

export const Dialog = DialogPrimitive.Root;
export const DialogTrigger = DialogPrimitive.Trigger;
export const DialogPortal = DialogPrimitive.Portal;
export const DialogClose = DialogPrimitive.Close;

export const DialogOverlay = forwardRef<
  React.ComponentRef<typeof DialogPrimitive.Overlay>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Overlay>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Overlay
    ref={ref}
    className={cn(
      "fixed inset-0 z-50 bg-black/70 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0",
      className,
    )}
    {...props}
  />
));
DialogOverlay.displayName = DialogPrimitive.Overlay.displayName;

/**
 * Tres anchos y nada mas. Si un dialogo necesita mas que `lg`, es una ruta.
 *
 * Van sin el prefijo `sm:` a proposito: en mobile el ancho ya lo acota
 * `w-[calc(100vw-2rem)]`, y sin prefijo un `className="max-w-md"` de los usos
 * viejos sigue pisando al default via tailwind-merge (con `sm:max-w-lg` de base
 * no lo pisaria y esos dialogos se ensancharian).
 */
const ANCHOS = {
  sm: "max-w-md", // 448: confirmaciones (Anular, Descartar, Revertir)
  md: "max-w-lg", // 512: formularios
  lg: "max-w-3xl", // 768: dos columnas, tablas, talon, wizard ancho
} as const;

export interface DialogContentProps extends React.ComponentPropsWithoutRef<
  typeof DialogPrimitive.Content
> {
  tamano?: keyof typeof ANCHOS;
  /** "hoja": ocupa la pantalla entera debajo de sm (cobro, selector de cliente, ticket). */
  mobile?: "centrado" | "hoja";
}

/**
 * El alto que se ve arriba del teclado del celular, o `null` si no hay teclado.
 *
 * En el iPhone el teclado no achica la página: tapa los 336 px de abajo. Un
 * cuadro a pantalla completa mide el alto de la pantalla, así que el campo con
 * el foco quedaba debajo del teclado, y para mostrarlo Safari corría la vista
 * entera y se llevaba la barra de arriba (Cancelar / Siguiente). Con el alto
 * del `visualViewport` el cuadro se achica a lo que se ve: la barra queda
 * arriba y el contenido se desliza hasta el campo.
 */
function useAltoArribaDelTeclado(activo: boolean): number | null {
  const [alto, setAlto] = useState<number | null>(null);
  useEffect(() => {
    const vv = typeof window !== "undefined" ? window.visualViewport : null;
    if (!activo || !vv) return;
    const medir = (): void => {
      const tapado = window.innerHeight - vv.height;
      setAlto(window.innerWidth < 640 && tapado > 80 ? Math.round(vv.height) : null);
    };
    medir();
    vv.addEventListener("resize", medir);
    return () => {
      vv.removeEventListener("resize", medir);
      setAlto(null);
    };
  }, [activo]);
  return alto;
}

/**
 * Contenedor `flex-col` con el padding y el scroll de siempre. Asi los
 * dialogos que meten el contenido directo (todos, hoy) siguen viendose igual, y
 * los que usan `DialogBody` obtienen header y footer fijos: el body es
 * `flex-1 min-h-0` con scroll propio, asi que el contenido entra justo en el
 * `max-h` y el contenedor nunca llega a scrollear.
 *
 * `bg-card` y no `bg-background`: en dark el dialogo quedaba mas oscuro que las
 * tarjetas de atras.
 */
export const DialogContent = forwardRef<
  React.ComponentRef<typeof DialogPrimitive.Content>,
  DialogContentProps
>(({ className, children, tamano = "md", mobile = "centrado", style, ...props }, ref) => {
  // En el celular, todo cuadro que no sea una confirmación chica ocupa la
  // pantalla entera, con Cancelar y el botón para seguir en una barra arriba
  // (las reglas están en globals.css, bajo `data-pantalla="completa"`). Así el
  // teclado, que tapa la mitad de abajo, nunca tapa cómo seguir: antes el pie
  // iba apilado abajo y quedaba debajo del teclado (Recibir equipo, Transferir,
  // Comprar dólares, el gasto de Caja, el alta de un cliente).
  const pantallaCompleta = mobile === "hoja" || tamano !== "sm";
  const altoVisible = useAltoArribaDelTeclado(pantallaCompleta);
  return (
    <DialogPortal>
      <DialogOverlay />
      <DialogPrimitive.Content
        ref={ref}
        data-pantalla={pantallaCompleta ? "completa" : "centrado"}
        style={altoVisible !== null ? { ...style, height: altoVisible } : style}
        className={cn(
          "fixed left-1/2 top-1/2 z-50 flex max-h-[calc(100dvh-2rem)] w-[calc(100vw-2rem)] -translate-x-1/2 -translate-y-1/2 flex-col gap-4 overflow-y-auto rounded-lg border bg-card p-4 shadow-lg duration-200 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 sm:p-6",
          ANCHOS[tamano],
          pantallaCompleta &&
            "max-sm:left-0 max-sm:top-0 max-sm:h-dvh max-sm:max-h-none max-sm:w-full max-sm:translate-x-0 max-sm:translate-y-0 max-sm:rounded-none max-sm:border-0 max-sm:pt-0",
          className,
        )}
        {...props}
      >
        {children}
        <DialogPrimitive.Close asChild>
          <IconButton
            icono={X}
            aria-label="Cerrar"
            data-cerrar=""
            // `z-20`: por encima de la barra fija de arriba (z-10) del celular.
            className="absolute right-2 top-2 z-20"
          />
        </DialogPrimitive.Close>
      </DialogPrimitive.Content>
    </DialogPortal>
  );
});
DialogContent.displayName = DialogPrimitive.Content.displayName;

/** `pr-10` deja lugar a la X (44px en `right-2`) para que el titulo no pase por debajo. */
export function DialogHeader({ className, ...props }: HTMLAttributes<HTMLDivElement>): JSX.Element {
  return <div className={cn("flex flex-col gap-1 pr-10 text-left", className)} {...props} />;
}

/**
 * La unica zona con scroll. Los margenes negativos la extienden hasta el borde
 * del dialogo (la barra de scroll queda pegada al borde y las bandas sticky de
 * adentro cubren el ancho entero) y el padding devuelve el contenido a su lugar.
 */
export function DialogBody({ className, ...props }: HTMLAttributes<HTMLDivElement>): JSX.Element {
  return (
    <div
      data-cuerpo=""
      className={cn(
        "-m-4 min-h-0 flex-1 space-y-4 overflow-y-auto p-4 sm:-mx-6 sm:px-6",
        className,
      )}
      {...props}
    />
  );
}

export interface DialogFooterProps extends HTMLAttributes<HTMLDivElement> {
  /** `wizard`: Cancelar a la izquierda, Atras + Siguiente a la derecha. */
  variant?: "default" | "wizard";
  /**
   * En el celular el pie sube a una barra arriba (ver globals.css). `abajo` lo
   * deja fijo abajo: para cuadros de botones sin teclado, como la orden de
   * reparación, donde seis acciones arriba ocupaban 165 px.
   */
  enCelular?: "arriba" | "abajo";
}

/**
 * ¿El pie trae un «Cancelar»? Entonces en el celular la ✕ sobra (hace lo
 * mismo) y se esconde para dejarle lugar a los botones. Si no lo trae, la ✕ es
 * la única salida y se queda: el buscador de clientes y la orden de reparación
 * llegaron a quedar sin forma de cerrarse.
 */
function tieneCancelar(nodo: ReactNode): boolean {
  let hay = false;
  Children.forEach(nodo, (hijo) => {
    if (hay || !isValidElement<{ children?: ReactNode }>(hijo)) return;
    const contenido = hijo.props.children;
    if (contenido === "Cancelar") hay = true;
    else if (contenido !== undefined && typeof contenido !== "string")
      hay = tieneCancelar(contenido);
  });
  return hay;
}

export function DialogFooter({
  className,
  variant = "default",
  enCelular = "arriba",
  ...props
}: DialogFooterProps): JSX.Element {
  return (
    <div
      data-pie={enCelular}
      data-con-cancelar={tieneCancelar(props.children) ? "" : undefined}
      className={cn(
        "flex flex-col-reverse gap-2 sm:flex-row",
        variant === "wizard" ? "sm:justify-between" : "sm:justify-end",
        className,
      )}
      {...props}
    />
  );
}

export const DialogTitle = forwardRef<
  React.ComponentRef<typeof DialogPrimitive.Title>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Title>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Title
    ref={ref}
    className={cn("text-base font-semibold leading-tight", className)}
    {...props}
  />
));
DialogTitle.displayName = DialogPrimitive.Title.displayName;

export const DialogDescription = forwardRef<
  React.ComponentRef<typeof DialogPrimitive.Description>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Description>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Description
    ref={ref}
    className={cn("text-sm text-muted-foreground", className)}
    {...props}
  />
));
DialogDescription.displayName = DialogPrimitive.Description.displayName;
